import React, { useRef, useState } from "react";
import useEventAttendeeListLogic, {
  EventAttendee,
} from "./EventAttendeeList.logic";
import {
  DataTable,
  DataTableExpandedRows,
  DataTableFilterMeta,
  DataTableValueArray,
} from "primereact/datatable";
import { Column, ColumnFilterElementTemplateOptions } from "primereact/column";
import { IconField } from "primereact/iconfield";
import { InputIcon } from "primereact/inputicon";
import { InputText } from "primereact/inputtext";
import { FilterMatchMode } from "primereact/api";
import { Button } from "primereact/button";
import { ColumnMeta, exportCSVManual } from "../../../utils/exportCsv";
import { exportEventPdf } from "../../../utils/exportEventPdf";
import { Dialog } from "primereact/dialog";
import { Tooltip } from "primereact/tooltip";
import { confirmPopup } from "primereact/confirmpopup";
import { Dropdown, DropdownChangeEvent } from "primereact/dropdown";
import { Tag } from "primereact/tag";
import QRCode from "qrcode";
import AddAttendeeForm from "../../../components/elements/AddAttendeeForm";

const EventAttendeeList: React.FC = () => {
  const {
    isAdmin,
    eventId,
    attendees,
    eventDetails,
    loading,
    navigate,
    paymentReceiptLoading,
    handleShowPaymentReceipt,
    totalDonationAmount,
    totalAmount,
    totalHostAmount,
    totalHostSettledAmount,
    totalHostUnSettledAmount,
    totalHostFailedAmount,
    handleMarkAsPaid,
    handleMarkAsUnPaid,
    isEventDateIsToday,
    attendeeLimit,
    attendeeRegistered,
    attendeeCheckedIn,
    resendEmailLoading,
    handleResendRegistrationEmail,
    fetchEventAttendees,
    checkIsEventDatePassed,
  } = useEventAttendeeListLogic();

  // ✅ new state for toggle
  const [showCancelledRefunded, setShowCancelledRefunded] = useState(false);

  // ✅ filtered attendees
  const filteredAttendees = attendees.filter((a) => {
    if (!showCancelledRefunded) {
      return a.status !== "cancelled" && a.status !== "refunded";
    }
    return true;
  });

  const statusBodyTemplate = (rowData: EventAttendee) => {
    return rowData.status === "registered" ? (
      <>
        <span className="p-tag p-tag-success">{rowData.status_label}</span>
      </>
    ) : rowData.status === "checked-in" ? (
      <>
        <span className="p-tag p-tag-info">{rowData.status_label}</span>
      </>
    ) : rowData.status === "refunded" ? (
      <>
        <span className="p-tag p-tag-warning">{rowData.status_label}</span>
      </>
    ) : (
      <span className="p-tag p-tag-danger">{rowData.status_label}</span>
    );
  };

  const amountBodyTemplate = (rowData: EventAttendee) => {
    return (
      <div>
        <div>
          {(rowData.amount ?? 0).toLocaleString("en-US", {
            style: "currency",
            currency: "USD",
          })}
        </div>
        {rowData.discount_amount > 0 && (
          <>
            <small className="text-muted">
              Discount:{" "}
              {rowData.discount_amount.toLocaleString("en-US", {
                style: "currency",
                currency: "USD",
              })}
            </small>
            <br />
          </>
        )}
        {rowData.host_amount > 0 && (
          <>
            <small className="text-muted">
              Host:{" "}
              {rowData.host_amount.toLocaleString("en-US", {
                style: "currency",
                currency: "USD",
              })}
            </small>
            <br />
          </>
        )}
        {rowData.donation_amount > 0 && (
          <>
            <small className="text-muted">
              Donation:{" "}
              {rowData.donation_amount.toLocaleString("en-US", {
                style: "currency",
                currency: "USD",
              })}
            </small>
            <br />
          </>
        )}
        {rowData.service_amount > 0 && (
          <>
            <small className="text-muted">
              Service Fee:{" "}
              {rowData.service_amount.toLocaleString("en-US", {
                style: "currency",
                currency: "USD",
              })}
            </small>
            <br />
          </>
        )}
        {rowData.payment_process_amount > 0 && (
          <>
            <small className="text-muted">
              Payment Process Fee:{" "}
              {rowData.payment_process_amount.toLocaleString("en-US", {
                style: "currency",
                currency: "USD",
              })}
            </small>
          </>
        )}
      </div>
    );
  };

  const [globalFilterValue, setGlobalFilterValue] = useState<string>("");

  const [filters, setFilters] = useState<DataTableFilterMeta>({
    global: { value: null, matchMode: FilterMatchMode.CONTAINS },
    name: { value: null, matchMode: FilterMatchMode.STARTS_WITH },
  });

  const onGlobalFilterChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    let _filters = { ...filters };

    // @ts-ignore
    _filters["global"].value = value;

    setFilters(_filters);
    setGlobalFilterValue(value);
  };

  const dt = useRef<DataTable<EventAttendee[]>>(null);

  const exportCSV = () => {
    const cols: ColumnMeta[] = [
      { field: "name", title: "Name" },
      { field: "email", title: "Email" },
      { field: "registered_by", title: "Registered By" },
      { field: "number_of_attendees", title: "Number of Attendee(s)" },
      { field: "registered_at", title: "Registered At" },
      { field: "cancelled_at", title: "Cancelled At" },
      { field: "amount", title: "Amount Paid" },
      { field: "donation_amount", title: "Donation" },
      { field: "status", title: "Registration Status" },
    ];

    exportCSVManual(
      filteredAttendees.map((v) => {
        const isManual = v.manual_attendee_email ? true : false;
        return {
          name: isManual ? v.manual_attendee_name : v.User?.name,
          email: isManual ? v.manual_attendee_email : v.User?.email,
          registered_by: isManual ? v.User?.name || "Host" : "Self",
          number_of_attendees: v.number_of_attendees,
          registered_at: v.registered_at_formatted,
          cancelled_at: v.cancelled_at_formatted,
          amount: v.amount,
          donation_amount: v.donation_amount,
          status: v.status_label,
        };
      }),
      cols,
      `event-attendees-${eventDetails.title}.csv`,
    );
  };

  const exportPdf = () => {
    exportEventPdf({
      docTitle: "Event Attendee List",
      tableHeaders: [
        "Sr.",
        "Name",
        "Email",
        "Registered By",
        "Number of Attendee(s)",
        "Registered At",
        "Cancelled At",
        "Amount Paid",
        "Donation",
        "Registration Status",
      ],
      tableData: filteredAttendees.map((v, i) => {
        const isManual = v.manual_attendee_email ? true : false;
        return [
          i + 1,
          isManual ? v.manual_attendee_name : v.User?.name,
          isManual ? v.manual_attendee_email : v.User?.email,
          isManual ? v.User?.name || "Host" : "Self",
          v.number_of_attendees,
          v.registered_at_formatted,
          v.cancelled_at_formatted,
          (v.amount ?? 0).toLocaleString("en-US", {
            style: "currency",
            currency: "USD",
          }),
          v.donation_amount.toLocaleString("en-US", {
            style: "currency",
            currency: "USD",
          }),
          v.status_label,
        ];
      }),
      eventDetails,
      filenamePrefix: "event-attendees",
    });
  };

  const renderHeader = () => {
    return (
      <>
        <div className="flex justify-content-start align-items-center mb-2 flex-column-responsive">
          <div className="left_searchs">
            <IconField iconPosition="left">
              <InputIcon className="fas fa-search" />
              <InputText
                value={globalFilterValue}
                onChange={onGlobalFilterChange}
                placeholder="Keyword Search"
              />
            </IconField>
          </div>
          <>
            <div className="left_btns ml-auto">
              <Button
                type="button"
                label={
                  showCancelledRefunded ? "Hide Cancelled" : "Show Cancelled"
                }
                icon={showCancelledRefunded ? "fas fa-eye-slash" : "fas fa-eye"}
                onClick={() => setShowCancelledRefunded((prev) => !prev)}
                className="alt_btn ms-2"
              />
              <Button
                type="button"
                icon="fas fa-file-csv"
                onClick={() => exportCSV()}
                tooltip="Export as CSV"
                tooltipOptions={{ position: "top" }}
                className="alt_btn ms-2"
              />
              <Button
                type="button"
                icon="fas fa-file-pdf"
                onClick={exportPdf}
                tooltip="Export as PDF"
                tooltipOptions={{ position: "top" }}
                className="alt_btn ms-2"
              />

              {totalAmount > 0 && (
                <>
                  <Button
                    type="button"
                    label="Event Breakdown"
                    icon="fas fa-dollar-sign"
                    onClick={() => onShowBreakdown()}
                    className="alt_btn ms-2"
                  />
                </>
              )}
              {/* {isEventDateIsToday && (
                <Button
                  type="button"
                  label="Check-In"
                  onClick={() => navigate(`check-in`)}
                  className="alt_btn ms-2"
                />
              )} */}
              <Button
                type="button"
                label="Back to Event Detail"
                onClick={() => navigate(-1)}
                className="alt_btn ms-2"
              />
            </div>
          </>
        </div>
      </>
    );
  };

  const header = renderHeader();

  const [expandedRows, setExpandedRows] = useState<
    DataTableExpandedRows | DataTableValueArray | undefined
  >(undefined);

  const allowExpansion = (rowData: EventAttendee) => {
    return true;
    // return rowData.payment_id !== null;
  };

  const rowExpansionTemplate = (rowData: EventAttendee) => {
    return (
      <div className="container-fluid py-3 row-expansion-template">
        <div className="row">
          <div className="col-12 col-md-4 mb-3 mb-md-0">
            <h3 className="fw-bold">Payment Ref.:</h3>
            <p className="mb-2">
              {rowData.payment_id || "N/A"} <br />
            </p>
          </div>
          {rowData.refund_id && (
            <div className="col-12 col-md-4 mb-3 mb-md-0">
              <h3 className="fw-bold">Refund Ref.:</h3>
              <p className="mb-2">{rowData.refund_id}</p>
            </div>
          )}
        </div>
        <div className="row">
          {rowData.HostPayoutQueues &&
            rowData.HostPayoutQueues.status_label && (
              <div className="col-12 col-md-4 mb-3 mb-md-0">
                <h3 className="fw-bold">Host Payout Ref.:</h3>
                <p>
                  <strong>Status:</strong>{" "}
                  {rowData.HostPayoutQueues.status_label}
                </p>
                <p>
                  <strong>Scheduled At:</strong>{" "}
                  {rowData.HostPayoutQueues.scheduled_at_formatted}
                </p>
                {rowData.HostPayoutQueues.processed_at_formatted && (
                  <p>
                    <strong>Processed At:</strong>{" "}
                    {rowData.HostPayoutQueues.processed_at_formatted || "N/A"}
                  </p>
                )}
                {rowData.HostPayoutQueues.stripe_transfer_id && (
                  <p>
                    <strong>Transation ID:</strong>{" "}
                    {rowData.HostPayoutQueues.stripe_transfer_id || "N/A"}
                  </p>
                )}
              </div>
            )}
          {rowData.HostPayoutQueues &&
            rowData.HostPayoutQueues.refund_status_label && (
              <div className="col-12 col-md-4 mb-3 mb-md-0">
                <h3 className="fw-bold">Host Payout Refund Ref.:</h3>
                <p>
                  <strong>Status</strong>:{" "}
                  {rowData.HostPayoutQueues.refund_status_label}
                </p>
                {rowData.HostPayoutQueues.refund_processed_at_formatted && (
                  <p>
                    <strong>Processed At:</strong>{" "}
                    {rowData.HostPayoutQueues.refund_processed_at_formatted ||
                      "N/A"}
                  </p>
                )}
                {rowData.HostPayoutQueues.refund_stripe_transfer_id && (
                  <p>
                    <strong>Transation ID:</strong>{" "}
                    {rowData.HostPayoutQueues.refund_stripe_transfer_id ||
                      "N/A"}
                  </p>
                )}
              </div>
            )}
        </div>
      </div>
    );
  };

  const eventUserBodyTemplates = (rowData: EventAttendee) => {
    const isManual = rowData.manual_attendee_email ? true : false;
    return (
      <>
        {rowData.manual_attendee_email ? (
          <div>
            <div>
              <strong>{rowData.manual_attendee_name}</strong>
            </div>
            <small className="text-muted">
              {rowData.manual_attendee_email}
            </small>
            <small className="text-muted d-block">
              (Registered by: {rowData.User.name})
            </small>
          </div>
        ) : (
          <div>
            <div>
              <strong>{rowData.User.name}</strong>
            </div>
            <small className="text-muted">{rowData.User.email}</small>
          </div>
        )}
      </>
    );
  };

  const eventUserBodyTemplate = (rowData: EventAttendee) => {
    const isManual = rowData.manual_attendee_email ? true : false;

    return (
      <div>
        <div>
          <strong>
            {isManual ? rowData.manual_attendee_name : rowData.User?.name}
          </strong>
        </div>

        <small className="text-muted">
          {isManual ? rowData.manual_attendee_email : rowData.User?.email}
        </small>

        {isManual && (
          <small className="text-muted d-block">
            Registered by: {rowData.User?.name || "Host"}
          </small>
        )}
      </div>
    );
  };

  const [qrVisible, setQrVisible] = useState(false);
  const [qrImage, setQrImage] = useState<string | null>(null);
  const [selectedRegistration, setSelectedRegistration] = useState<
    string | null
  >(null);

  const handleGenerateQR = async (token: string, name: string) => {
    const data = JSON.stringify({ booking_token: token });
    try {
      const qrDataUrl = await QRCode.toDataURL(data, {
        width: 300,
        margin: 2,
      });
      setQrImage(qrDataUrl);
      setSelectedRegistration(name || null);
      setQrVisible(true);
    } catch (err) {
      console.error("Error generating QR:", err);
    }
  };

  const actionBodyTemplate = (rowData: EventAttendee) => {
    return (
      <>
        {rowData.registration_token && rowData.status === "registered" && (
          <Button
            icon="pi pi-qrcode"
            rounded
            tooltip="View QR Code"
            tooltipOptions={{ position: "top" }}
            onClick={() =>
              handleGenerateQR(
                rowData.registration_token || "",
                rowData.User.name,
              )
            }
          />
        )}
        {rowData.status === "registered" && (
          <Button
            icon={
              resendEmailLoading ? "fas fa-spinner fa-spin" : "fas fa-envelope"
            }
            rounded
            onClick={() => handleResendRegistrationEmail(rowData.id)}
            tooltip={
              resendEmailLoading
                ? "Sending..."
                : "Resend Registration Confirmation Email"
            }
            tooltipOptions={{ position: "top" }}
            disabled={resendEmailLoading}
          ></Button>
        )}
        {rowData.amount > 0 && (
          <Button
            icon={
              paymentReceiptLoading
                ? "fas fa-spinner fa-spin"
                : "fas fa-receipt"
            }
            rounded
            onClick={() =>
              handleShowPaymentReceipt(rowData.user_id, rowData.id)
            }
            tooltip={
              paymentReceiptLoading ? "Fetching..." : "View Payment Receipt"
            }
            tooltipOptions={{ position: "top" }}
            disabled={paymentReceiptLoading}
          ></Button>
        )}
      </>
    );
  };

  const [visibleBreakdown, setVisibleBreakdown] = useState(false);
  const onShowBreakdown = () => {
    setVisibleBreakdown(true);
  };
  const onHideBreakdown = () => {
    setVisibleBreakdown(false);
  };

  return (
    <>
      <section className="schedule-three ngo_list_wrapper event_attendee_list">
        <div className="container">
          <div className="schedule-three__top d-flex align-items-center justify-content-between flex-column-responsive">
            <div
              className="section-title text-center wow fadeInLeft"
              data-wow-delay="100ms"
            >
              <h2 className="section-title__title">Event Registrations</h2>
            </div>
            <div className="check-in-count">
              {isEventDateIsToday ? (
                <p>
                  <strong>Checked-In Attendees: </strong>{" "}
                  {attendeeCheckedIn ?? 0}/{attendeeRegistered ?? 0}{" "}
                </p>
              ) : (
                <p>
                  <strong>Total Registered Attendees: </strong>{" "}
                  {attendeeRegistered ?? 0}/{attendeeLimit ?? 0}{" "}
                  {(attendeeRegistered ?? 0) < (attendeeLimit ?? 0) &&
                    !checkIsEventDatePassed() && (
                      <AddAttendeeForm
                        eventId={eventId ?? 0}
                        availableSeats={
                          (attendeeLimit ?? 0) - (attendeeRegistered ?? 0)
                        }
                        eventDetails={{
                          title: eventDetails?.title || "",
                        }}
                        onRegistered={() => fetchEventAttendees(eventId ?? 0)}
                      />
                    )}
                </p>
              )}
            </div>
          </div>
          <div className="schedule-three__bottom">
            <DataTable
              ref={dt}
              value={filteredAttendees}
              expandedRows={expandedRows}
              onRowToggle={(e) => setExpandedRows(e.data)}
              rowExpansionTemplate={rowExpansionTemplate}
              removableSort
              sortField="registered_at"
              sortOrder={-1}
              showGridlines
              paginator
              rows={5}
              loading={loading}
              rowsPerPageOptions={[5, 10, 25, 50]}
              filters={filters}
              // filterDisplay="row"
              globalFilterFields={["User.name", "User.email"]}
              header={header}
              emptyMessage="No attendees found."
              onFilter={(e) => setFilters(e.filters)}
              paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
              currentPageReportTemplate="Showing {first} to {last} of {totalRecords} Event Registrations"
              tableStyle={{ minWidth: "50rem" }}
            >
              <Column expander={allowExpansion} style={{ width: "5rem" }} />
              <Column
                field="User.name"
                header="Name"
                body={eventUserBodyTemplate}
                sortable
              ></Column>
              <Column
                field="number_of_attendees"
                header="Attendee(s)"
                sortable
              ></Column>
              <Column
                field="registered_at"
                header="Registered At"
                body={(rowData: EventAttendee) =>
                  rowData.registered_at_formatted
                }
                sortable
              ></Column>
              <Column
                field="cancelled_at_formatted"
                header="Cancelled At"
                sortable
              ></Column>
              <Column
                headerStyle={{ width: "15%", minWidth: "8rem" }}
                field="amount"
                header="Amount Paid"
                bodyStyle={{ textAlign: "center" }}
                body={amountBodyTemplate}
                headerClassName="custom-status-column"
                sortable
              ></Column>
              <Column
                headerStyle={{ width: "10%", minWidth: "8rem" }}
                field="status_label"
                header="Registration Status"
                bodyStyle={{ textAlign: "center" }}
                body={statusBodyTemplate}
                headerClassName="custom-status-column"
                sortable
              ></Column>
              <Column
                headerStyle={{ width: "15%", minWidth: "8rem" }}
                bodyStyle={{ textAlign: "center" }}
                headerClassName="custom-status-column"
                header="Actions"
                body={actionBodyTemplate}
              ></Column>
            </DataTable>
          </div>
        </div>
      </section>
      <Dialog
        header="Event Breakdown"
        visible={visibleBreakdown}
        style={{ width: "40vw" }}
        onHide={onHideBreakdown}
        draggable={false}
      >
        <div>
          <p className="mb-1">
            <strong>Event:</strong> {eventDetails?.title}
          </p>
          <p className="mt-3 mb-1">
            <strong>Collected Amount:</strong> $
            {totalAmount ? totalAmount.toFixed(2) : "0.00"}
          </p>
          <p className="mt-3 mb-1">
            <strong>Amount to Host:</strong> ${totalHostAmount.toFixed(2)}
            <br />
            <small className="text-muted">
              (<strong>Settled:</strong> ${totalHostSettledAmount.toFixed(2)} |{" "}
              <strong>Unsettled:</strong> ${totalHostUnSettledAmount.toFixed(2)}{" "}
              | <strong>Failed:</strong> ${totalHostFailedAmount.toFixed(2)})
            </small>
          </p>
          <p className="mt-3 mb-1">
            <strong>Amount to Donate:</strong> ${totalDonationAmount.toFixed(2)}
          </p>
          <p className="mt-3 mb-2">
            <strong>Net Amount:</strong> $
            {(totalAmount - totalDonationAmount).toFixed(2)}
          </p>
          {totalDonationAmount > 0 && (
            <div className="ngo-img mt-3 mb-2">
              <strong>NGOs:</strong>
              {eventDetails?.event_ngos.map((ngo: any, index: number) => (
                <p key={index} className="mt-1">
                  {index + 1}
                  {". "}
                  <a href={ngo.ngo.donation_link} target="_blank" key={index}>
                    {ngo.ngo.name}{" "}
                    <i className="fas fa-external-link-alt"></i>{" "}
                  </a>
                  {ngo.is_paid ? (
                    <>
                      {"- "}
                      <a
                        className="text-decoration-underline"
                        onClick={() => handleMarkAsUnPaid(ngo.ngo_id)}
                      >
                        Mark as Unpaid
                      </a>{" "}
                      <span className="p-tag p-tag-success">Donation Paid</span>
                    </>
                  ) : (
                    <>
                      {"- "}
                      <a
                        className="text-decoration-underline"
                        onClick={() => handleMarkAsPaid(ngo.ngo_id)}
                      >
                        Mark as Paid
                      </a>
                    </>
                  )}
                </p>
              ))}
            </div>
          )}
        </div>
      </Dialog>
      <Dialog
        visible={qrVisible}
        showHeader={true}
        header={`QR Code for ${selectedRegistration || "Event"}`}
        position="center"
        onHide={() => {
          if (!qrVisible) return;
          setQrVisible(false);
        }}
        draggable={false}
        resizable={false}
      >
        <div className="col-12 d-flex align-items-center justify-content-center mt-3">
          {qrImage ? (
            <>
              <img
                src={qrImage}
                alt="QR Code"
                style={{ width: 300, height: 300 }}
              />
            </>
          ) : (
            <p>Generating QR...</p>
          )}
        </div>
        <div className="col-12 d-flex align-items-center justify-content-center mt-3">
          <p>Scan this QR code at the event entrance for check-in.</p>
        </div>
        <div className="col-12 d-flex align-items-center justify-content-center mt-3">
          <Button
            label="Close"
            outlined
            onClick={() => setQrVisible(false)}
            className="thm-black-btn small_btn"
          />
        </div>
      </Dialog>
    </>
  );
};

export default EventAttendeeList;
